import {
  Container,
  Group,
  Stack,
  Text,
  Title,
  type MantineSize,
} from "@mantine/core";
import clsx from "clsx";
import { Fragment } from "react";
import { Helmet } from "react-helmet";
import { ScrollRestoration, type Location } from "react-router-dom";
import classes from "./Page.module.css";
import { BackButton } from "./buttons/BackButton";

interface PageDetails {
  title?: string;
  titleLeftSection?: React.JSX.Element;
  titleRightSection?: React.JSX.Element;
  description?: string;
}

export function PageMeta({ title, description }: PageDetails) {
  const canonicalUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, "")}`;

  return (
    <Helmet>
      <title>{title ? `NOAN | ${title}` : "NOAN"}</title>

      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:url" content={document.location.toString()} />
      <meta name="description" content={description} />
      <meta property="og:description" content={description} />
    </Helmet>
  );
}

function getScrollRestorationKey(location: Location) {
  return location.pathname;
}

export function Page({
  children,
  setPageMeta = true,
  title = "",
  containerSize,
  titleLeftSection,
  titleRightSection,
  description,
  documentTitle,
  pageActions,
  backButton,
  aboveTitleSection = null,
  renderDescription,
  renderTitle,
  withAssistant = false,
}: React.PropsWithChildren<
  PageDetails & {
    setPageMeta?: boolean;

    /** Leaves room at the bottom for the floating assistant trigger */
    withAssistant?: boolean;

    /** If set, is used as the document's title instead of the title property */
    documentTitle?: string;

    /** Render arbitrary content above the title */
    aboveTitleSection?: React.JSX.Element | null;

    size?: MantineSize;
    containerSize?: MantineSize;
    pageActions?: React.JSX.Element[];

    /**
     * Allows controlling rendering for specific elements, while preserving the
     * string values for opengraph/metadata:
     */
    renderDescription?: (description: string) => React.JSX.Element;
    renderTitle?: (title: string) => React.JSX.Element;

    backButton?: {
      label: string;
      to?: string;
    };
  }
>) {
  return (
    <Container
      classNames={{
        root: clsx(
          classes.container,
          !containerSize && classes.containerWithWidth,
        ),
      }}
      size={containerSize}
    >
      {setPageMeta && (
        <PageMeta title={documentTitle ?? title} description={description} />
      )}

      {backButton && (
        <div className={classes.backButton}>
          <BackButton
            label={backButton.label}
            to={backButton.to}
            visibleFrom="md"
          />
        </div>
      )}

      {aboveTitleSection && (
        <div className={classes.aboveTitleSection}>{aboveTitleSection}</div>
      )}

      {(title || description || pageActions) && (
        <Group gap="sm" align="center" mb="lg">
          {(title || description) && (
            <Stack gap={5} flex={1}>
              {title && (
                <Group>
                  {titleLeftSection}
                  <Title order={1} size={32} fw={600}>
                    {renderTitle ? renderTitle(title) : title}
                  </Title>
                  {titleRightSection}
                </Group>
              )}

              {description && (
                <Text maw="65ch" size="sm">
                  {renderDescription
                    ? renderDescription(description)
                    : description}
                </Text>
              )}
            </Stack>
          )}

          {pageActions && pageActions.length > 0 && (
            <Group gap="xs">
              {pageActions.map((p, i) => (
                <Fragment key={i}>{p}</Fragment>
              ))}
            </Group>
          )}
        </Group>
      )}

      {withAssistant ? (
        <div className="scrolls-under-assistant">{children}</div>
      ) : (
        children
      )}
      <ScrollRestoration getKey={getScrollRestorationKey} />
    </Container>
  );
}
